import Linkify from "linkify-react";
import type { ReactNode } from "react";
import type { MessageMedia } from "@/api/types/message";
import { cn } from "@/lib/utils";
import { MessageProps } from "../homepage/types";

export const Message = ({
  body,
  bodyContent,
  direction,
  media = [],
  className = "",
  clickable = false,
}: MessageProps & { clickable?: boolean; bodyContent?: ReactNode }) => {
  const isOutgoing = direction === "outgoing";

  const visualMedia = media.filter(
    (m) => m.contentType.includes("image") || m.contentType.includes("video"),
  );
  const vcardMedia = media.filter((m) => m.contentType.includes("vcard"));
  const otherMedia = media.filter(
    (m) =>
      !m.contentType.includes("image") &&
      !m.contentType.includes("video") &&
      !m.contentType.includes("vcard"),
  );

  const hasBody = !!body || !!bodyContent;
  const hasOtherMedia = otherMedia.length > 0;
  const showTextBubble = hasBody || hasOtherMedia;

  const bubbleClasses = cn(
    isOutgoing
      ? "text-white border-transparent bg-outgoing-bubble rounded-br-md"
      : "bg-incoming-bubble border-border text-foreground rounded-bl-md",
    "border py-2 px-3 rounded-2xl text-xs sm:leading-snug sm:text-sm inline-block text-left whitespace-pre-line overflow-hidden max-w-xs",
    className,
  );

  return (
    <div className={cn(isOutgoing ? "text-right" : "text-left", "w-full mb-3", className)}>
      {visualMedia.map((m: MessageMedia) => (
        <div
          key={m.url}
          className={cn(
            isOutgoing ? "rounded-br-md ml-auto" : "rounded-bl-md",
            "rounded-3xl overflow-hidden mb-1 max-w-[75%]",
          )}
        >
          {m.contentType.includes("image") ? (
            <img alt="MMS" className="block w-full" src={m.url} />
          ) : (
            <video className="block w-full pointer-events-none" preload="metadata">
              <source src={m.url} type={m.contentType} />
            </video>
          )}
        </div>
      ))}

      {vcardMedia.map((m: MessageMedia) => (
        <div key={m.url} className={cn(isOutgoing ? "ml-auto" : "", "max-w-[200px] mb-1")}>
          <PreviewContactCard />
        </div>
      ))}

      {showTextBubble && (
        <div className={bubbleClasses}>
          {otherMedia.map((m: MessageMedia) => {
            if (m.contentType.includes("audio")) {
              return (
                <audio
                  key={m.url}
                  className="block w-full mb-2 rounded-lg"
                  preload="metadata"
                  controls
                >
                  <source src={m.url} type={m.contentType} />
                </audio>
              );
            }
            return (
              <a
                key={m.url}
                href={m.url}
                target="_blank"
                rel="noreferrer"
                className="underline break-all"
              >
                Attachment.{m.contentType.split("/").pop()}
              </a>
            );
          })}
          {hasBody && (
            <Linkify
              options={{
                className: cn("underline", !clickable && "pointer-events-none"),
                target: "_blank",
              }}
            >
              {bodyContent ?? body}
            </Linkify>
          )}
        </div>
      )}
    </div>
  );
};

function PreviewContactCard() {
  return (
    <div className="relative m-2 flex min-w-[200px] flex-col items-start rounded-md border border-border bg-background px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
          <img alt="" src="/images/icon.png" className="size-5 object-contain" aria-hidden="true" />
        </div>
        <div className="flex flex-col items-start gap-0">
          <div className="text-sm font-semibold">Helios</div>
          <div className="text-xs text-muted-foreground">Contact card</div>
        </div>
      </div>
    </div>
  );
}
